import {useDraggable, useDroppable} from '@dnd-kit/core';
import {Show} from 'platform/foundation';

import {
  GROUP_EDGE_LINE_STYLE,
  GROUP_HEADER_CELL_BORDER_BOTTOM,
} from '../../../../../../constants/borderStyles';
import {useColumnMovingAllowed} from '../../../../../../store/selectors';
import type {GroupRowCell} from '../../../../../../utils/groupRowModel';

export type GroupHeaderCellProps = {
  cell: GroupRowCell;
  isLast: boolean;
};

const CELL_STICKY_STYLE: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 'var(--z-content-loader)',
  ...GROUP_HEADER_CELL_BORDER_BOTTOM,
};

/**
 * One cell of the group header row.
 *
 * Group cells are draggable (moving the whole group of columns as a block) and
 * droppable; ungrouped placeholder cells are drop targets only, so a group can
 * be dropped before/after any column. Drag visuals are applied via the injected
 * <style> element scoped by `data-group-cell` (see useGridDragAndDrop) — this
 * component never re-renders during a drag.
 */
export function GroupHeaderCell(props: GroupHeaderCellProps) {
  const columnMovingAllowed = useColumnMovingAllowed();
  const isGroupCell = props.cell.type === 'group';

  // Both hooks tolerate a missing DndContext (columnMovingAllowed false) —
  // same contract as useSortable in HeaderCell.
  const {setNodeRef: setDropRef} = useDroppable({id: props.cell.id});
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: props.cell.id,
    disabled: !isGroupCell || !columnMovingAllowed,
  });

  if (props.cell.type !== 'group') {
    return (
      <th
        ref={setDropRef}
        colSpan={1}
        className="relative bg-gray-50"
        style={CELL_STICKY_STYLE}
        data-group-cell={props.cell.id}
      >
        <Show when={!props.isLast}>
          <span style={GROUP_EDGE_LINE_STYLE} />
        </Show>
      </th>
    );
  }

  return (
    <th
      ref={(node) => {
        setDropRef(node);
        setDragRef(node);
      }}
      colSpan={props.cell.columnKeys.length}
      className={`relative text-xs font-semibold text-gray-500 text-center bg-gray-50 truncate px-2 py-1 select-none ${
        columnMovingAllowed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : '' // eslint-disable-line no-nested-ternary -- Pre-existing violation
      }`}
      style={CELL_STICKY_STYLE}
      {...attributes}
      role="columnheader" // eslint-disable-line jsx-a11y/no-redundant-roles -- Muted pre-existing violation
      data-group-cell={props.cell.id}
      data-group-id={props.cell.groupId}
      {...listeners}
    >
      {props.cell.headerName}
      <Show when={!props.isLast}>
        <span style={GROUP_EDGE_LINE_STYLE} />
      </Show>
    </th>
  );
}
